import * as React from "react"
import { cn } from "@/lib/utils"
import { ChevronDown } from "lucide-react"

/**
 * NavigableInput - An input field with a down arrow that appears when user starts typing.
 * Clicking the arrow moves focus to the next editable field.
 * 
 * Props:
 * - nextFieldRef: ref to the next input field to focus
 * - nextFieldId: ID of the next input field (alternative to ref)
 * - onNavigateNext: callback function when down arrow is clicked
 * - All standard input props
 */
const NavigableInput = React.forwardRef(({ 
  className, 
  type,
  nextFieldRef,
  nextFieldId,
  onNavigateNext,
  value,
  inputMode,
  borderClassName, // Allow custom border class override
  ...props 
}, ref) => {
  const [hasValue, setHasValue] = React.useState(!!value);
  const inputRef = React.useRef(null);
  const combinedRef = ref || inputRef;

  // Automatically set inputMode for number types to show numeric keyboard on mobile
  const resolvedInputMode = inputMode || (type === 'number' ? 'decimal' : undefined);

  // Determine border class - use custom if provided, otherwise default behavior
  const getBorderClass = () => {
    if (borderClassName) {
      return borderClassName;
    }
    return hasValue ? "border-slate-900" : "border-input";
  };

  // Update hasValue when value changes
  React.useEffect(() => {
    setHasValue(!!value && String(value).length > 0);
  }, [value]);

  const handleNavigateNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // First, try the callback
    if (onNavigateNext) {
      onNavigateNext();
      return;
    }
    
    // Then try the ref
    if (nextFieldRef?.current) {
      nextFieldRef.current.focus();
      return;
    }
    
    // Then try the ID
    if (nextFieldId) {
      const nextElement = document.getElementById(nextFieldId);
      if (nextElement) {
        nextElement.focus();
        return;
      }
    }
    
    // Fallback: find next focusable input in the form
    const currentInput = combinedRef?.current || document.activeElement;
    if (currentInput) {
      const form = currentInput.closest('form') || currentInput.closest('.space-y-3');
      if (form) {
        const inputs = Array.from(form.querySelectorAll(
          'input:not([disabled]):not([readonly]):not([type="hidden"]):not([type="file"]), select:not([disabled]), textarea:not([disabled])'
        ));
        const currentIndex = inputs.indexOf(currentInput);
        if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
          const nextInput = inputs[currentIndex + 1];
          nextInput.focus();
          // If it's a select trigger, click it
          if (nextInput.getAttribute('role') === 'combobox') {
            nextInput.click();
          }
        }
      }
    }
  };

  return (
    <div className="relative w-full">
      <input
        type={type}
        value={value}
        inputMode={resolvedInputMode}
        pattern={type === 'number' ? '[0-9]*' : undefined}
        className={cn(
          "flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          hasValue ? "pr-10" : "", // Add padding for arrow when has value
          getBorderClass(),
          className
        )}
        ref={combinedRef}
        {...props}
      />
      {/* Down arrow - only visible when user has entered data */}
      {hasValue && (
        <button
          type="button"
          onClick={handleNavigateNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 active:text-slate-800 transition-colors touch-manipulation"
          tabIndex={-1}
          aria-label="Go to next field"
        >
          <ChevronDown className="w-5 h-5 animate-bounce" style={{ animationDuration: '1.5s' }} />
        </button>
      )}
    </div>
  );
});

NavigableInput.displayName = "NavigableInput"

export { NavigableInput }
